<template>
  <div class="controls">
    <div class="controls__start">
      <button
        v-tooltip="'Navigate to training session manager'"
        class="button"
        aria-label="Back"
        @click="() => $router.push({name: `${isPublic ? 'public.' : ''}trainings.list`, params: {expId: encodeID(currentExperiment.id, 'exp')}})"
      >
        <SVGIcon
          class="me-1"
          :iconName="'chevron_left'"
          :width="'18px'"
          :height="'18px'"
        />
        <span>All Sessions</span>
      </button>
    </div>
  </div>
  <div v-if="!chartsReady " class="wait-wrapper">
    <div id="loading-visualization"><div class="lds-ring"><div /><div /><div /><div /></div></div>
  </div>
  <div v-else-if="chartsReady && Object.keys(chartsData).length > 0" id="wrapper" class="chart-wrapper scrollbar">
    <div v-for="chart in chartsData" :key="chart.name" class="chart-small">
      <TrainingChartSingle
        :name="chart.name"
        :labels="chart.labels"
        :series="chart.series"
        @chart-clicked="chartZoomHandler(chart.name)"
      />
    </div>
  </div>
  <div v-else-if="chartsReady && Object.keys(chartsData).length === 0" id="wrapper" class="chart-wrapper scrollbar">
    No charts in training sessions
  </div>
</template>

<script>
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import TrainingChartSingle from '@/components/TrainerComponent/TrainerExperimentAndSessions/TrainingChartSingle';
import SVGIcon from '@/components/SVGIcon.vue';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import { encodeID } from '@/assets/js/utils.js';

const $toast = useToast();

export default {
  name: 'TrainingChartsPage',
  components: {
    TrainingChartSingle,
    SVGIcon,
  },
  inject: {
    isPublic: { default: false },
  },
  data() {
    return {
      dataConnect: new DatastoreConnect(this.$store.state.enterpriseServerUrl),
      chartsReady: false,
      chartsData: {},
    };
  },
  computed: {
    currentProject() {
      return this.$store.state.projects.currentProject;
    },
    currentExperiment: {
      get() {
        return this.$store.state.experiments.currentExperiment;
      },
      set(exp) {
        this.$store.commit('experiments/setCurrentExperiment', exp);
      },
    },
  },
  watch: {
    async currentExperiment() {
      this.GetChartsData();
    },
  },
  async mounted() {
    // Get exp_id from route params (which is the encoded format)
    const expIdParam = this.$route.params.expId;
    
    if (!expIdParam && this.currentExperiment) {
      this.$router.replace({ name: this.$route.name, params: { ...this.$route.params, expId: encodeID(this.currentExperiment.id, 'exp') } });
    }

    this.interval = setInterval(() => {
      this.GetChartsData();
    }, 5000);

    if (this.currentExperiment) {
      await this.GetChartsData();
    }
  },
  unmounted() {
    clearInterval(this.interval);
  },
  methods: {
    async handleGetExperiment(id) {
      await this.dataConnect.getExperiment({ trainer_id: id })
        .then(async (data) => {
          if (data.result) {
            this.currentExperiment = data.result;
          } else if (data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Failed to retrieve experiment: ${data.error.message}`,
              type: "error",
              position: "top",
            });
          }
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve experiment: ${error}`,
            type: "error",
            position: "top",
          });
        });
    },

    async GetChartsData() {
      this.fetchCharts().then(async (resp) => {
        const localChartsData = [];
        this.chartsReady = false;

        for (const [chartName, charts] of Object.entries(resp)) {
          const chart_data = {};
          const series = [];
          let labels = []; // final x-axis labels

          // Determine the longest label array
          for (const chart of charts) {
            if (chart.data.labels.length > labels.length) {
              labels = chart.data.labels;
            }
          }

          // Build series with x/y pairs
          for (const chart of charts) {
            const y = chart.data.series;

            // zip labels and series, pad with null if shorter
            const zippedData = labels.map((lx, i) => ({
              x: lx,
              y: y[i] ?? null, // use null if series is shorter than labels
            }));

            series.push({
              name: chart.id,
              data: zippedData,
            });
          }

          chart_data['series'] = series;
          chart_data['labels'] = labels;
          chart_data['name'] = chartName;

          localChartsData.push(chart_data);
        }

        this.chartsData = localChartsData;
        this.chartsReady = true;
      });
    },
    async fetchCharts() {
      if (!this.currentExperiment?.id) return {};

      // Get Training Charts from BE
      const params = {
        trainer_id: this.currentExperiment.id,
      };

      const resp = await this.dataConnect.getCharts(params)
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve charts: ${error}`,
            type: "error",
            position: "top",
          });
        });
      if (!resp || resp.error || !resp.result) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to retrieve charts: ${resp.error.message}`,
          type: "error",
          position: "top",
        });
        return {};
      }
      return resp.result;
    },
  },
};
</script>

<style lang="scss" scoped>
.controls {
  display: flex;
  margin: 20px 20px 5px 20px;
  align-items: center;

  &__count {
    font-weight: bold;
    font-size: 1rem;
  }

  &__start {
    display: flex;
    justify-content: flex-start;
    width: fit-content
  }

  &__end {
    display: flex;
    justify-content: flex-end;
    flex: 1;
  }

  &__actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    height: 100%;
  }
}

.button {
    background: white;
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 26px;
    padding: 0;
    box-shadow: 1px 2px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 1);
    padding: 4px 8px;
    gap: 2px;
    width: fit-content;

    @include themify() {
      color: themed('body-text-color')
    }

    &.highlight {
      background: var(--button-color);
      color: var(--button-text-color);

      svg {
        margin-right: 5px;
      }
    }

    &:hover {
      background: rgba(var(--color-primary-100-rgb), 0.5);
    }

    &.delete:not([disabled]) {
      @include themify() {
        background: themed('button-delete');
        color: themed('button-delete-text');
      }
    }

    &.delete[disabled] {
      border: none;
    }

    &.delete:hover:not([disabled]) {
      @include themify() {
        background: $color-red-300;
        color: themed('button-delete-text');
      }
    }
  }

.chart-wrapper {
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: row;
  flex-wrap: wrap;
  padding: 8px;
  background-color: #f4f3f2;
  align-content: start;
  overflow-y: auto;
}

.chart-row {
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: row;
  flex-wrap: nowrap;
  padding: 8px;
  background-color: #f4f3f2;
  align-content: start;
  overflow-x: scroll;
}

.wait-wrapper {
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  background-color: #f4f3f2;
}

.chart-small{
  border-radius: 8px;
  width: 600px;
  min-width: 400px;
  height: 350px;
  background-color: white;
  flex-grow: 1;

  margin: 8px;
}

.chart-full {
  width: 100%;
  height: 100%;
}
</style>
